import { Car, ClipboardCheck, Fuel, ShieldAlert, Wrench } from 'lucide-react';

/**
 * Maquete da interface, para o hero da landing.
 *
 * É uma representação em CSS, não um screenshot. Três motivos:
 *
 *  - **Fidelidade honesta sem depender de dados.** Um print do painel exigiria
 *    uma conta com frota grande e bonita; sem isso o produto parece vazio. A
 *    maquete mostra a estrutura real (sidebar escura, KPIs, tabela de frota)
 *    sem inventar números de cliente.
 *  - **Acompanha o tema.** Usa os mesmos tokens do app, então funciona em
 *    claro e escuro. Um PNG ficaria errado num dos dois.
 *  - **Nítida em qualquer tela.** Sem imagem borrada em Retina, sem 300 KB
 *    de bundle.
 *
 * Marcada como `aria-hidden`: é ilustração. Quem usa leitor de tela recebe o
 * conteúdo pelo texto ao redor, não por aqui.
 */
export function ProductMockup() {
  return (
    <div
      aria-hidden="true"
      className="relative select-none"
      style={{ perspective: '1400px' }}
    >
      {/* Brilho difuso atrás, para a maquete descolar do fundo */}
      <div className="pointer-events-none absolute -inset-x-8 -top-8 bottom-0 rounded-[2rem] bg-gradient-to-b from-primary/10 to-transparent blur-2xl" />

      <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-2xl shadow-primary/10">
        {/* Barra do navegador: dá contexto de "isto é o painel na web" */}
        <div className="flex items-center gap-2 border-b border-border bg-muted/60 px-3 py-2.5">
          <div className="flex gap-1.5">
            <span className="size-2.5 rounded-full bg-destructive/40" />
            <span className="size-2.5 rounded-full bg-warning/50" />
            <span className="size-2.5 rounded-full bg-success/50" />
          </div>
          <div className="ml-2 flex-1 rounded-md bg-background/80 px-3 py-1 text-center font-mono text-[10px] text-muted-foreground">
            app.iabordo.com.br
          </div>
        </div>

        <div className="flex">
          <MockSidebar />
          <MockConteudo />
        </div>
      </div>

      {/* Celular sobreposto: o app do motorista, que é metade do produto.
          Fica mais abaixo e mais à direita do que o instinto pede, porque o
          conteúdo do painel vai até a borda — sobreposto mais acima, ele
          tapava a coluna de status da tabela de frota. O `pb` do conteúdo
          reserva o espaço vazio que ele ocupa. */}
      <div className="absolute -bottom-10 -right-5 hidden w-[124px] overflow-hidden rounded-[1.25rem] border-4 border-foreground/85 bg-card shadow-2xl sm:block">
        <MockCelular />
      </div>
    </div>
  );
}

function MockSidebar() {
  const itens = [
    { icone: Car, rotulo: 'Veículos', ativo: true },
    { icone: ClipboardCheck, rotulo: 'Checklists' },
    { icone: Fuel, rotulo: 'Abastecimento' },
    { icone: Wrench, rotulo: 'Manutenção' },
    { icone: ShieldAlert, rotulo: 'Multas' },
  ];

  return (
    <div className="hidden w-36 shrink-0 bg-[hsl(var(--sidebar))] p-3 sm:block">
      <div className="mb-4 flex items-center gap-1.5">
        <div className="size-4 rounded bg-success" />
        <span className="text-[10px] font-semibold text-[hsl(var(--sidebar-foreground))]">
          IA Bordo
        </span>
      </div>
      <div className="space-y-0.5">
        {itens.map((item) => (
          <div
            key={item.rotulo}
            className={`flex items-center gap-1.5 rounded px-1.5 py-1 text-[9px] ${
              item.ativo
                ? 'bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-foreground))]'
                : 'text-[hsl(var(--sidebar-foreground))]/55'
            }`}
          >
            <item.icone className="size-2.5" />
            {item.rotulo}
          </div>
        ))}
      </div>
    </div>
  );
}

function MockConteudo() {
  const kpis = [
    { rotulo: 'Veículos', valor: '24' },
    { rotulo: 'Checklists hoje', valor: '18' },
    { rotulo: 'Manutenções', valor: '3' },
  ];

  // Estados que existem de verdade no produto — nada inventado.
  const frota = [
    { placa: 'RTM4C82', modelo: 'Volvo FH 460', estado: 'ok' as const, texto: 'Em dia' },
    { placa: 'KLP7D31', modelo: 'Scania R450', estado: 'alerta' as const, texto: 'Revisão em 400 km' },
    { placa: 'HGT9E14', modelo: 'Iveco Daily', estado: 'erro' as const, texto: 'CNH vencendo' },
  ];

  const cor = {
    ok: 'bg-success',
    alerta: 'bg-warning',
    erro: 'bg-destructive',
  };

  return (
    <div className="flex-1 space-y-3 p-3 sm:p-4 sm:pb-14">
      <div className="grid grid-cols-3 gap-2">
        {kpis.map((kpi) => (
          <div key={kpi.rotulo} className="rounded-lg border border-border bg-background p-2">
            <p className="text-[8px] uppercase tracking-wide text-muted-foreground">
              {kpi.rotulo}
            </p>
            <p className="font-mono text-base font-semibold tabular-nums">{kpi.valor}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="border-b border-border bg-muted/50 px-2.5 py-1.5 text-[8px] font-medium uppercase tracking-wide text-muted-foreground">
          Frota
        </div>
        {frota.map((v) => (
          <div
            key={v.placa}
            className="flex items-center gap-2 border-b border-border/60 px-2.5 py-1.5 last:border-0"
          >
            <span className={`size-1.5 shrink-0 rounded-full ${cor[v.estado]}`} />
            <span className="font-mono text-[9px] font-medium">{v.placa}</span>
            <span className="hidden truncate text-[9px] text-muted-foreground sm:inline">
              {v.modelo}
            </span>
            <span className="ml-auto whitespace-nowrap text-[8px] text-muted-foreground">
              {v.texto}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockCelular() {
  const itens = [
    { rotulo: 'Pneus', ok: true },
    { rotulo: 'Freios', ok: true },
    { rotulo: 'Luzes', ok: false },
  ];

  return (
    <div className="bg-card p-2">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[7px] font-semibold">Checklist</span>
        <span className="font-mono text-[7px] text-muted-foreground">2/3</span>
      </div>
      <div className="mb-2 h-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full w-2/3 rounded-full bg-success" />
      </div>
      <div className="space-y-1">
        {itens.map((item) => (
          <div
            key={item.rotulo}
            className="flex items-center justify-between rounded border border-border px-1.5 py-1"
          >
            <span className="text-[7px]">{item.rotulo}</span>
            <span
              className={`size-2 rounded-full ${item.ok ? 'bg-success' : 'bg-destructive'}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
